import './ReviewStep.css';
import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, X, AlertTriangle, RefreshCw, FileText, Inbox, Target } from 'lucide-react';
import useVoiceDumpStore from '../../stores/useVoiceDumpStore';
import useResourceStore from '../../stores/useResourceStore';

const CATEGORY_ICONS = {
  tasks: '✓',
  projects: '📁',
  areas: '📍',
  resources: '📄',
  flows: '📋',
  timer: '⏱️',
  identities: '🎭',
};

function formatDuration(seconds) {
  if (!seconds) return '?';
  const mins = Math.round(seconds / 60);
  return mins >= 60 ? `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ''}` : `${mins}m`;
}

const URGENCY_CONFIG = {
  must_do: { emoji: '🔴', label: 'Must Do', className: 'must-do' },
  should_do: { emoji: '🟡', label: 'Should Do', className: 'should-do' },
  could_do: { emoji: '🟢', label: 'Could Do', className: 'could-do' },
};

const URGENCY_ORDER = ['must_do', 'should_do', 'could_do'];

function getActionTypeLabel(type) {
  if (type === 'set_active_identity') return 'ACTIVATE';
  if (type === 'complete_task') return 'DONE';
  if (type === 'add_subtasks') return 'SUBTASKS';
  if (type === 'prioritize_task') return 'PRIORITY';
  if (type.startsWith('create')) return 'CREATE';
  if (type.startsWith('update')) return 'UPDATE';
  if (type.startsWith('delete')) return 'DELETE';
  return type.toUpperCase();
}

function getActionTypeCss(type) {
  if (type === 'set_active_identity') return 'update';
  if (type === 'complete_task') return 'update';
  if (type.startsWith('create')) return 'create';
  if (type.startsWith('update')) return 'update';
  if (type.startsWith('delete')) return 'delete';
  return 'create';
}

function getConfidenceLevel(confidence) {
  if (!confidence && confidence !== 0) return null;
  if (confidence >= 80) return 'high';
  if (confidence >= 50) return 'medium';
  return 'low';
}

export default function ReviewStep() {
  const transcript = useVoiceDumpStore(s => s.transcript);
  const actions = useVoiceDumpStore(s => s.actions);
  const summary = useVoiceDumpStore(s => s.summary);
  const error = useVoiceDumpStore(s => s.error);
  const toggleAction = useVoiceDumpStore(s => s.toggleAction);
  const removeAction = useVoiceDumpStore(s => s.removeAction);
  const executeActions = useVoiceDumpStore(s => s.executeActions);
  const selectAll = useVoiceDumpStore(s => s.selectAll);
  const deselectAll = useVoiceDumpStore(s => s.deselectAll);
  const reRecord = useVoiceDumpStore(s => s.reRecord);
  const closeWizard = useVoiceDumpStore(s => s.closeWizard);

  const [showTranscript, setShowTranscript] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [collapsedTiers, setCollapsedTiers] = useState({});

  // Check if actions have urgency tiers (for backward compatibility)
  const hasUrgencyTiers = useMemo(() =>
    actions.some(a => a.urgency && URGENCY_ORDER.includes(a.urgency)),
    [actions]
  );

  // Group by urgency tier, then category within each tier
  const urgencyGroups = useMemo(() => {
    if (!hasUrgencyTiers) return null;

    const groups = {};
    URGENCY_ORDER.forEach(tier => { groups[tier] = []; });

    actions.forEach(action => {
      const tier = URGENCY_ORDER.includes(action.urgency) ? action.urgency : 'could_do';
      groups[tier].push(action);
    });

    return groups;
  }, [actions, hasUrgencyTiers]);

  // Legacy category grouping (fallback when AI doesn't return urgency)
  const categoryGroups = useMemo(() => {
    if (hasUrgencyTiers) return null;
    const groups = {};
    actions.forEach(action => {
      const cat = action.category || 'tasks';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(action);
    });
    return groups;
  }, [actions, hasUrgencyTiers]);

  // Auto-collapse "Could Do" if there are Must/Should actions
  const isTierCollapsed = (tier) => {
    if (collapsedTiers[tier] !== undefined) return collapsedTiers[tier];
    // Auto-collapse could_do if there are higher priority actions
    if (tier === 'could_do' && urgencyGroups) {
      const hasHigherPriority = (urgencyGroups.must_do?.length > 0) || (urgencyGroups.should_do?.length > 0);
      return hasHigherPriority;
    }
    return false;
  };

  const toggleTier = (tier) => {
    setCollapsedTiers(prev => ({ ...prev, [tier]: !isTierCollapsed(tier) }));
  };

  const selectedCount = actions.filter(a => a.selected).length;
  const selectedDeleteCount = actions.filter(a => a.type?.includes('delete') && a.selected).length;

  // Graceful fallback: save transcript as Quick Capture note
  const handleSaveToQuickCapture = () => {
    const store = useResourceStore.getState();
    store.addResource({
      title: `Voice Dump — ${new Date().toLocaleString()}`,
      type: 'note',
      content: transcript,
      tags: ['voice-dump'],
      status: 'active',
    });
    closeWizard();
  };

  // Batch DELETE guard
  const handleApprove = () => {
    if (selectedDeleteCount > 0) {
      setShowDeleteConfirm(true);
    } else {
      executeActions();
    }
  };

  // Render a single action card
  const renderAction = (action) => {
    const typeCss = getActionTypeCss(action.type);
    const confidenceLevel = getConfidenceLevel(action.confidence);

    return (
      <div
        key={action.id}
        className={`vd-action vd-action--${typeCss} ${!action.selected ? 'vd-action--deselected' : ''}`}
      >
        <input
          type="checkbox"
          className="vd-action__checkbox"
          checked={action.selected}
          onChange={() => toggleAction(action.id)}
          aria-label={`Select: ${action.label}`}
        />

        <div className="vd-action__content">
          <div className="vd-action__label">{action.label}</div>
          <div className="vd-action__meta">
            <span className={`vd-action__badge vd-action__badge--${typeCss}`}>
              {getActionTypeLabel(action.type)}
            </span>

            {action.category && (
              <span className="vd-action__badge vd-action__badge--cat">
                {CATEGORY_ICONS[action.category] || '📦'} {action.category}
              </span>
            )}

            {/* Confidence indicator for UPDATE/DELETE */}
            {confidenceLevel && (action.type?.includes('update') || action.type?.includes('delete')) && (
              <span className={`vd-action__confidence vd-action__confidence--${confidenceLevel}`}>
                <Target size={10} />
                {action.confidence}%
              </span>
            )}

            {action.data?.matchTitle && (
              <span className="vd-action__match">
                matched: {action.data.matchTitle}
              </span>
            )}
            {action.data?.matchName && (
              <span className="vd-action__match">
                matched: {action.data.matchName}
              </span>
            )}
            {action.data?.priority && (
              <span className="vd-action__badge vd-action__badge--create">
                {action.data.priority}
              </span>
            )}
            {action.data?.dueDate && (
              <span className="vd-action__badge vd-action__badge--update">
                📅 {action.data.dueDate}
              </span>
            )}
            {action.data?.scheduledDate && (
              <span className="vd-action__badge vd-action__badge--update">
                🗓️ {action.data.scheduledDate}
              </span>
            )}
            {action.data?.projectName && (
              <span className="vd-action__match">
                → {action.data.projectName}
              </span>
            )}
            {action.data?.areaName && (
              <span className="vd-action__match">
                📍 {action.data.areaName}
              </span>
            )}
          </div>

          {/* Flow step preview */}
          {action.type === 'create_flow' && action.data?.steps?.length > 0 && (
            <div className="vd-action__preview vd-flow-preview">
              <div className="vd-flow-preview__timeline">
                {action.data.steps.map((step, i) => (
                  <div key={i} className="vd-flow-preview__step">
                    <span className="vd-flow-preview__focus">
                      🎯 {formatDuration(step.focusDuration)}
                    </span>
                    {step.taskNames?.length > 0 && (
                      <span className="vd-flow-preview__tasks">
                        {step.taskNames.join(', ')}
                      </span>
                    )}
                    <span className="vd-flow-preview__rest">
                      ☕ {formatDuration(step.restDuration)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="vd-flow-preview__total">
                Total: {formatDuration(action.data.steps.reduce((sum, s) => sum + (s.focusDuration || 0) + (s.restDuration || 0), 0))}
              </div>
            </div>
          )}

          {/* Identity settings preview */}
          {action.type === 'create_identity' && action.data?.settings?.durations && (
            <div className="vd-action__preview vd-identity-preview">
              <div className="vd-identity-preview__bars">
                <div className="vd-identity-preview__bar" title="Focus">
                  <span className="vd-identity-preview__icon">🎯</span>
                  <div className="vd-identity-preview__fill vd-identity-preview__fill--focus"
                    style={{ width: `${Math.min(100, (action.data.settings.durations.focus || 3000) / 54)}%` }} />
                  <span className="vd-identity-preview__label">{formatDuration(action.data.settings.durations.focus)}</span>
                </div>
                <div className="vd-identity-preview__bar" title="Rest">
                  <span className="vd-identity-preview__icon">☕</span>
                  <div className="vd-identity-preview__fill vd-identity-preview__fill--rest"
                    style={{ width: `${Math.min(100, (action.data.settings.durations.rest || 600) / 54)}%` }} />
                  <span className="vd-identity-preview__label">{formatDuration(action.data.settings.durations.rest)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Priority reasoning */}
          {action.priorityReason && (
            <div className="vd-action__reason">{action.priorityReason}</div>
          )}
        </div>

        <button
          className="btn btn-ghost btn-sm btn-icon vd-action__remove"
          onClick={() => removeAction(action.id)}
          title="Remove action"
          aria-label={`Remove: ${action.label}`}
        >
          <X size={14} />
        </button>
      </div>
    );
  };

  // Error state
  if (error && actions.length === 0) {
    return (
      <div className="vd-review">
        <div className="vd-error">
          <AlertTriangle size={16} />
          {error === 'QUOTA_EXCEEDED'
            ? 'Gemini API quota exceeded. Please try again later.'
            : error === 'MALFORMED_RESPONSE'
              ? 'AI returned an invalid response. Try re-recording.'
              : `Processing failed: ${error.replace('API_ERROR: ', '')}`}
        </div>

        {transcript && (
          <button className="btn btn-tertiary" onClick={handleSaveToQuickCapture} style={{ marginTop: 'var(--space-sm)' }}>
            <Inbox size={16} /> Save Transcript to Quick Capture
          </button>
        )}

        <div className="vd-footer">
          <button className="btn btn-tertiary" onClick={reRecord}>
            <RefreshCw size={16} /> Re-record
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (actions.length === 0) {
    return (
      <div className="vd-review">
        {/* Transcript */}
        {transcript && (
          <div className="vd-transcript">
            <div className="vd-transcript__header" onClick={() => setShowTranscript(!showTranscript)}>
              <span className="vd-transcript__title">
                <FileText size={14} /> Transcript
              </span>
              {showTranscript ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </div>
            {showTranscript && (
              <div className="vd-transcript__body">{transcript}</div>
            )}
          </div>
        )}

        <div className="vd-empty">
          <div className="vd-empty__icon">💡</div>
          <p className="vd-empty__text">
            No actionable items found. Try being more specific about tasks, projects, or goals you'd like to accomplish.
          </p>
        </div>

        <div className="vd-footer" style={{ justifyContent: 'center', gap: 'var(--space-sm)' }}>
          <button className="btn btn-tertiary" onClick={reRecord}>
            <RefreshCw size={16} /> Re-record
          </button>
          {transcript && (
            <button className="btn btn-ghost" onClick={handleSaveToQuickCapture}>
              <Inbox size={16} /> Save to Quick Capture
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="vd-review" style={{ position: 'relative' }}>
      {/* Batch DELETE Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="vd-confirm-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="vd-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="vd-confirm-dialog__icon">⚠️</div>
            <h3 className="vd-confirm-dialog__title">Confirm Deletion</h3>
            <p className="vd-confirm-dialog__desc">
              You are about to <strong>delete {selectedDeleteCount} item{selectedDeleteCount > 1 ? 's' : ''}</strong>. This action cannot be undone.
            </p>
            <div className="vd-confirm-dialog__actions">
              <button className="btn btn-ghost" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={() => { setShowDeleteConfirm(false); executeActions(); }}>
                Delete & Approve All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transcript (collapsible) */}
      {transcript && (
        <div className="vd-transcript">
          <div className="vd-transcript__header" onClick={() => setShowTranscript(!showTranscript)}>
            <span className="vd-transcript__title">
              <FileText size={14} /> Transcript
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={(e) => { e.stopPropagation(); reRecord(); }}
                style={{ fontSize: 'var(--font-xs)' }}
              >
                <RefreshCw size={12} /> Re-record
              </button>
              {showTranscript ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </div>
          </div>
          {showTranscript && (
            <div className="vd-transcript__body">{transcript}</div>
          )}
        </div>
      )}

      {/* Summary */}
      {summary && <div className="vd-summary">{summary}</div>}

      {/* Urgency-grouped actions (new) */}
      {hasUrgencyTiers && urgencyGroups && URGENCY_ORDER.map(tier => {
        const items = urgencyGroups[tier];
        if (!items || items.length === 0) return null;
        const config = URGENCY_CONFIG[tier];
        const collapsed = isTierCollapsed(tier);

        return (
          <div key={tier} className={`vd-urgency-group vd-urgency-group--${config.className}`}>
            <button
              className="btn btn-tertiary vd-urgency-header"
              onClick={() => toggleTier(tier)}
              type="button"
            >
              <span className="vd-urgency-header__left">
                <span className="vd-urgency-header__emoji">{config.emoji}</span>
                <span className="vd-urgency-header__label">{config.label}</span>
                <span className="vd-urgency-header__count">{items.length}</span>
              </span>
              {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            </button>

            {!collapsed && (
              <div className="vd-urgency-body">
                {items.map(renderAction)}
              </div>
            )}
          </div>
        );
      })}

      {/* Legacy category-grouped actions (fallback) */}
      {!hasUrgencyTiers && categoryGroups && ['tasks', 'projects', 'areas', 'identities', 'resources', 'flows', 'timer'].map(cat => {
        const items = categoryGroups[cat];
        if (!items || items.length === 0) return null;

        return (
          <div key={cat} className="vd-category">
            <div className="vd-category__header">
              <span>{CATEGORY_ICONS[cat]}</span>
              <span>{cat}</span>
              <span className="vd-category__count">({items.length})</span>
            </div>
            {items.map(renderAction)}
          </div>
        );
      })}

      {/* Footer */}
      <div className="vd-footer vd-footer--spread">
        <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
          <button className="btn btn-ghost btn-sm" onClick={selectAll}>Select All</button>
          <button className="btn btn-ghost btn-sm" onClick={deselectAll}>Deselect All</button>
        </div>
        <button
          className={selectedDeleteCount > 0 ? 'btn btn-danger' : 'btn btn-primary'}
          onClick={handleApprove}
          disabled={selectedCount === 0}
        >
          {selectedDeleteCount > 0
            ? `⚠️ Approve ${selectedCount} (${selectedDeleteCount} delete)`
            : `Approve ${selectedCount} Actions`
          }
        </button>
      </div>
    </div>
  );
}
